import { SortOrder } from './SortOrder';
import ArrayUtils from './ArrayUtils';
import Collection from './Collection';
import DefaultComparer from '../system/DefaultComparer';
import DefaultEqualityComparer from '../system/DefaultEqualityComparer';
import IterableUtils from './IterableUtils';
import NumberUtils from '../primitives/NumberUtils';
import StringUtils from '../text/StringUtils';
export default class EnumerableGenerators {
    static *after(enumerable, predicate) {
        let predicateFound = false;
        for (const item of enumerable) {
            if (!predicateFound && predicate(item)) {
                predicateFound = true;
            }
            else if (predicateFound) {
                yield item;
            }
        }
    }
    static *afterInclude(enumerable, predicate) {
        let predicateFound = false;
        for (const item of enumerable) {
            if (!predicateFound && predicate(item)) {
                predicateFound = true;
            }
            if (predicateFound) {
                yield item;
            }
        }
    }
    static *append(enumerable, value) {
        yield* enumerable;
        if (IterableUtils.isIterable(value)) {
            yield* value;
        }
        else {
            yield value;
        }
    }
    static *batch(enumerable, size) {
        let batch = new Collection();
        for (const item of enumerable) {
            batch.push(item);
            if (batch.length === size) {
                yield batch;
                batch = new Collection();
            }
        }
        if (batch.length > 0) {
            yield batch;
        }
    }
    static *before(enumerable, predicate) {
        const items = [];
        for (const item of enumerable) {
            if (predicate(item)) {
                break;
            }
            items.push(item);
        }
        yield* items;
    }
    static *beforeInclude(enumerable, predicate) {
        const items = [];
        for (const item of enumerable) {
            items.push(item);
            if (predicate(item)) {
                break;
            }
        }
        yield* items;
    }
    static *clone(enumerable) {
        for (const item of enumerable) {
            yield item;
        }
    }
    static *combinationsWith(enumerable, other) {
        for (const item of enumerable) {
            for (const otherItem of other) {
                yield [item, otherItem];
            }
        }
    }
    static *distinct(enumerable, equalityComparer = DefaultEqualityComparer.equal) {
        const distinctItems = [];
        for (const item of enumerable) {
            let exists = false;
            for (const distinctItem of distinctItems) {
                if (equalityComparer(item, distinctItem)) {
                    exists = true;
                    continue;
                }
            }
            if (!exists) {
                distinctItems.push(item);
                yield item;
            }
        }
    }
    static *distinctConsecutives(enumerable, equalityComparer = DefaultEqualityComparer.equal, keepLast = false) {
        let previous;
        let index = 0;
        for (const item of enumerable) {
            if (previous === undefined || !equalityComparer(previous, item) || (keepLast && index == this.length - 1)) {
                yield item;
            }
            previous = item;
            index++;
        }
    }
    static *do(enumerable, action) {
        for (const item of enumerable) {
            action(item);
            yield item;
        }
    }
    static *evenItems(enumerable) {
        let isEven = true;
        for (const item of enumerable) {
            if (isEven) {
                yield item;
            }
            isEven = !isEven;
        }
    }
    static *except(enumerable, other, equalityComparer = DefaultEqualityComparer.equal) {
        for (const item1 of enumerable) {
            let exists = false;
            for (const item2 of other) {
                if (equalityComparer(item1, item2)) {
                    exists = true;
                }
            }
            if (!exists) {
                yield item1;
            }
        }
    }
    static *intersect(enumerable, other, equalityComparer = DefaultEqualityComparer.equal) {
        for (const item1 of enumerable) {
            let exists = false;
            for (const item2 of other) {
                if (equalityComparer(item1, item2)) {
                    exists = true;
                }
            }
            if (exists) {
                yield item1;
            }
        }
    }
    static *oddItems(enumerable) {
        let isOdd = false;
        for (const item of enumerable) {
            if (isOdd) {
                yield item;
            }
            isOdd = !isOdd;
        }
    }
    static *orderBy(enumerable, keySelector, comparer = DefaultComparer.compare, order = SortOrder.Ascending) {
        if (order === SortOrder.None) {
            return yield* enumerable;
        }
        yield* Array.from(enumerable).sort((a, b) => {
            const aVal = order === SortOrder.Ascending ? keySelector(a) : keySelector(b);
            const bVal = order === SortOrder.Ascending ? keySelector(b) : keySelector(a);
            if (Array.isArray(aVal) && Array.isArray(bVal)) {
                return ArrayUtils.compare(aVal, bVal);
            }
            else {
                return comparer(aVal, bVal);
            }
        });
    }
    static *orderByDescending(enumerable, keySelector, comparer = DefaultComparer.compare) {
        yield* EnumerableGenerators.orderBy(enumerable, keySelector, comparer, SortOrder.Descending);
    }
    static *orderByLike(enumerable, keySelector, order, equalityComparer = DefaultEqualityComparer.equal) {
        const orderArray = Array.from(order);
        yield* Array.from(enumerable).sort((a, b) => {
            return NumberUtils.compare(orderArray.findIndex(o => equalityComparer(o, keySelector(a))), orderArray.findIndex(o => equalityComparer(o, keySelector(b))));
        });
    }
    static *select(enumerable, selector) {
        for (const item of enumerable) {
            yield selector(item);
        }
    }
    static *selectMany(enumerable, selector) {
        for (const item of enumerable) {
            for (const nestedItem of selector(item)) {
                yield nestedItem;
            }
        }
    }
    static *skip(enumerable, count) {
        let skipped = 0;
        for (const item of enumerable) {
            if (skipped >= count) {
                yield item;
            }
            skipped++;
        }
    }
    static *sorted(enumerable, order = SortOrder.Ascending, comparer = DefaultComparer.compare) {
        if (order === SortOrder.Ascending) {
            yield* Array.from(enumerable).sort((a, b) => comparer(a, b));
        }
        else if (order === SortOrder.Descending) {
            yield* Array.from(enumerable).sort((a, b) => comparer(b, a));
        }
        else {
            yield* enumerable;
        }
    }
    static *sortedLike(enumerable, order, equalityComparer = DefaultEqualityComparer.equal) {
        const orderArray = Array.from(order);
        yield* Array.from(enumerable).sort((a, b) => {
            const indexOfA = orderArray.findIndex(o => equalityComparer(o, a));
            const indexOfB = orderArray.findIndex(o => equalityComparer(o, b));
            if (indexOfA === -1 && indexOfB !== -1) {
                return 1;
            }
            else if (indexOfA !== -1 && indexOfB === -1) {
                return -1;
            }
            else {
                return NumberUtils.compare(indexOfA, indexOfB);
            }
        });
    }
    static *take(enumerable, count) {
        let taken = 0;
        for (const item of enumerable) {
            if (taken < count) {
                yield item;
            }
            else {
                break;
            }
            taken++;
        }
    }
    static *union(enumerable, other, equalityComparer = DefaultEqualityComparer.equal) {
        yield* enumerable.append(other).distinct(equalityComparer);
    }
    static *when(enumerable, condition, then) {
        if (condition) {
            yield* then(enumerable);
        }
        else {
            yield* enumerable;
        }
    }
    static *where(enumerable, predicate) {
        for (const item of enumerable) {
            if (predicate(item)) {
                yield item;
            }
        }
    }
    static *whereAnd(enumerable, ...predicates) {
        for (const item of enumerable) {
            let allTrue = true;
            for (const predicate of predicates) {
                if (!predicate(item)) {
                    allTrue = false;
                    break;
                }
            }
            if (allTrue) {
                yield item;
            }
        }
    }
    static *whereIn(enumerable, selector, values) {
        for (const item1 of enumerable) {
            const value = selector(item1);
            let exists = false;
            for (const item2 of values) {
                if (value === item2) {
                    exists = true;
                }
            }
            if (exists) {
                yield item1;
            }
        }
    }
    static *whereNotIn(enumerable, selector, values) {
        for (const item1 of enumerable) {
            const value = selector(item1);
            let exists = false;
            for (const item2 of values) {
                if (value === item2) {
                    exists = true;
                }
            }
            if (!exists) {
                yield item1;
            }
        }
    }
    static *whereNotNull(enumerable, predicate) {
        for (const item of enumerable) {
            const result = predicate(item);
            if (result !== undefined && result !== null) {
                yield item;
            }
        }
    }
    static *whereNull(enumerable, predicate) {
        for (const item of enumerable) {
            const result = predicate(item);
            if (result === undefined || result === null) {
                yield item;
            }
        }
    }
    static *whereOr(enumerable, ...predicates) {
        for (const item of enumerable) {
            let oneTrue = false;
            for (const predicate of predicates) {
                if (!predicate(item)) {
                    oneTrue = true;
                    break;
                }
            }
            if (oneTrue) {
                yield item;
            }
        }
    }
    static *whereStringEquals(enumerable, value1Selector, value2Selector, caseSensitive = true) {
        for (const item of enumerable) {
            const selectedValue1 = value1Selector(item);
            const selectedValue2 = typeof value2Selector === 'function' ? value2Selector(item) : value2Selector;
            if (StringUtils.equal(selectedValue1, selectedValue2, false)) {
                yield item;
            }
        }
    }
}
//# sourceMappingURL=EnumerableGenerators.js.map